/**
 * Reducer to extract unique project types from projects array
 * @param {Array} projects - Array of project objects
 * @returns {Array} - Array of unique project types
 */
export const getUniqueProjectTypes = (projects = []) => {
  const types = new Set();
  
  projects.forEach(project => {
    if (project.projectType) {
      types.add(project.projectType);
    }
  });
  
  return Array.from(types).sort();
};

/**
 * Reducer to create filter options from project types
 * @param {Array} projects - Array of project objects
 * @returns {Array} - Array of filter options with id, label, and selected state
 */
export const createFilterOptions = (projects = []) => {
  const uniqueTypes = getUniqueProjectTypes(projects);
  
  return uniqueTypes.map((type) => ({
    id: type.toLowerCase().replace(/\s+/g, '-'),
    label: type,
    selected: false,
    count: projects.filter(p => p.projectType === type).length,
  }));
};

/**
 * Reducer to filter projects based on selected types
 * @param {Array} projects - Array of project objects
 * @param {Array} selectedTypes - Array of selected type labels
 * @returns {Array} - Filtered projects
 */
export const filterProjectsByType = (projects = [], selectedTypes = []) => {
  if (selectedTypes.length === 0) {
    return projects;
  }
  
  return projects.filter(project => selectedTypes.includes(project.projectType));
};

/**
 * Reducer for filter state management
 * @param {Object} state - Current filter state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} - Updated filter state
 */
export const filterReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FILTERS':
      return {
        ...state,
        filters: action.payload,
      };
    
    case 'TOGGLE_FILTER':
      return {
        ...state,
        filters: state.filters.map(filter =>
          filter.id === action.payload
            ? { ...filter, selected: !filter.selected }
            : filter
        ),
      };
    
    case 'SELECT_FILTER':
      return {
        ...state,
        filters: state.filters.map(filter =>
          filter.id === action.payload
            ? { ...filter, selected: true }
            : filter
        ),
      };
    
    case 'DESELECT_FILTER':
      return {
        ...state,
        filters: state.filters.map(filter =>
          filter.id === action.payload
            ? { ...filter, selected: false }
            : filter
        ),
      };
    
    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: state.filters.map(filter => ({ ...filter, selected: false })),
      };
    
    case 'SELECT_ALL_FILTERS':
      return {
        ...state,
        filters: state.filters.map(filter => ({ ...filter, selected: true })),
      };
    
    default:
      return state;
  }
};

/**
 * Get selected filter types from filter state
 * @param {Array} filters - Array of filter objects
 * @returns {Array} - Array of selected filter labels
 */
export const getSelectedFilters = (filters = []) => {
  return filters
    .filter(filter => filter.selected)
    .map(filter => filter.label);
};